import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  computed,
  effect,
  inject,
  signal,
} from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { toSignal } from "@angular/core/rxjs-interop";
import { filter, map, startWith } from "rxjs";
import { AuthService } from "../../services/auth.service";
import { LegalConsentService } from "../../services/legal-consent.service";
import { apiErrorMessage } from "../../../shared/format/api-error";
import {
  LEGAL_DOCUMENTS,
  LegalDocumentType,
} from "../../../models/legal.model";

/**
 * Aceite obrigatório (spec 029 §3). Abre sobre qualquer tela enquanto houver
 * documento pendente — no primeiro acesso, para quem já usava a plataforma
 * antes desta atualização, e quando sai versão que exige novo aceite.
 *
 * **Bloqueante de propósito:** sem ✕, sem fechar pelo fundo nem pelo Esc. As
 * únicas saídas são aceitar ou sair da conta. Por isso não usa o app-modal,
 * cujo ✕ é fixo (spec 026).
 */
@Component({
  selector: "app-legal-consent-modal",
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (open()) {
    <div
      class="fixed inset-0 z-[60] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center sm:p-4"
      animate.enter="bf-fade-in"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="legal-consent-title"
        class="flex max-h-[92dvh] w-full max-w-lg flex-col gap-5 overflow-y-auto rounded-t-2xl bg-[var(--bf-surface)] p-6 text-[var(--bf-text)] shadow-2xl sm:rounded-2xl sm:p-8"
        animate.enter="bf-sheet-in"
      >
        <div class="flex flex-col gap-2">
          <h2 id="legal-consent-title" class="font-display text-xl font-semibold">
            {{ isUpdate() ? "Atualizamos nossos documentos" : "Antes de continuar" }}
          </h2>
          <p class="text-sm leading-relaxed text-[var(--bf-text-muted)]">
            Para usar a plataforma, leia e aceite os documentos abaixo. Cada link abre o texto
            completo em uma nova aba.
          </p>
        </div>

        <ul class="flex flex-col gap-4">
          @for (doc of pending(); track doc.type) {
          <li class="flex flex-col gap-1 rounded-xl border border-[var(--bf-border)] p-4">
            <label class="flex min-h-11 cursor-pointer items-start gap-3 text-sm">
              <input
                type="checkbox"
                class="mt-0.5 size-5 shrink-0 cursor-pointer accent-[var(--bf-primary)]"
                [checked]="isChecked(doc.type)"
                [attr.aria-invalid]="showErrors() && !isChecked(doc.type)"
                (change)="toggle(doc.type)"
              />
              <span>
                Li e aceito {{ info(doc.type).acceptLabel }}
                <span class="text-[var(--bf-text-muted)]">(versão {{ doc.version }})</span>.
              </span>
            </label>
            <a
              [href]="info(doc.type).route"
              target="_blank"
              rel="noopener"
              class="ml-8 self-start text-sm text-[var(--bf-link)] underline underline-offset-4 hover:text-[var(--bf-link-hover)]"
            >
              Ler {{ info(doc.type).label }}
            </a>
            @if (showErrors() && !isChecked(doc.type)) {
            <p class="ml-8 text-xs bf-field-error" role="alert">Marque para continuar.</p>
            }
          </li>
          }
        </ul>

        @if (error(); as message) {
        <p
          role="alert"
          class="rounded-xl border border-[var(--bf-danger-border)] bg-[var(--bf-danger-bg)] p-3 text-sm text-[var(--bf-danger-text)]"
        >
          {{ message }}
        </p>
        }

        <div class="flex flex-col gap-3 sm:flex-row-reverse">
          <button
            type="button"
            class="bf-cta-gold inline-flex min-h-11 flex-1 items-center justify-center rounded-lg px-5 font-semibold disabled:opacity-60"
            [disabled]="saving()"
            (click)="accept()"
          >
            {{ saving() ? "Registrando..." : "Aceitar e continuar" }}
          </button>
          <button
            type="button"
            class="bf-btn-secondary inline-flex min-h-11 items-center justify-center rounded-lg px-5 font-medium"
            [disabled]="saving()"
            (click)="logout()"
          >
            Sair
          </button>
        </div>
      </div>
    </div>
    }
  `,
})
export class LegalConsentModal {
  private readonly consent = inject(LegalConsentService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  readonly pending = this.consent.pending;
  readonly open = computed(
    () =>
      this.pending().length > 0 && !this.consent.isLegalPage(this.url()),
  );
  /** Versão > 1.0 pendente = atualização, não primeiro acesso. */
  readonly isUpdate = computed(() =>
    this.pending().some((doc) => doc.version !== "1.0"),
  );

  readonly checked = signal<ReadonlySet<LegalDocumentType>>(new Set());
  readonly showErrors = signal(false);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);

  constructor() {
    // A página por baixo não rola enquanto o aceite está aberto.
    effect(() => {
      this.document.body.style.overflow = this.open() ? "hidden" : "";
    });
  }

  info(type: LegalDocumentType) {
    return LEGAL_DOCUMENTS[type];
  }

  isChecked(type: LegalDocumentType): boolean {
    return this.checked().has(type);
  }

  toggle(type: LegalDocumentType): void {
    const next = new Set(this.checked());
    if (next.has(type)) next.delete(type);
    else next.add(type);
    this.checked.set(next);
  }

  /** Validação inline: aceitar sem marcar tudo mostra o que falta. */
  async accept(): Promise<void> {
    const types = this.pending().map((doc) => doc.type);
    if (!types.every((type) => this.isChecked(type))) {
      this.showErrors.set(true);
      return;
    }
    this.saving.set(true);
    this.error.set(null);
    try {
      await this.consent.accept(types);
      this.checked.set(new Set());
      this.showErrors.set(false);
    } catch (error) {
      this.error.set(
        apiErrorMessage(error, "Não foi possível registrar o aceite. Tente de novo."),
      );
    } finally {
      this.saving.set(false);
    }
  }

  logout(): void {
    this.auth.logout();
  }
}
